/** Request/response types for generalized request analytics custom charts. */

import type { StructuredFilterGroupRequest } from "@/components/StructuredSearch";
import type { AnalyticsChartInterval } from "@/queries/useRequestAnalytics";

export enum CustomChartMetric {
  Count = "count",
  Sum = "sum",
  Avg = "avg",
  Min = "min",
  Max = "max",
  Percentile = "percentile",
  DistinctCount = "distinct_count",
}

export enum CustomChartSeriesMetric {
  Count = "count",
  Sum = "sum",
  Avg = "avg",
  Min = "min",
  Max = "max",
  Percentile = "percentile",
  DistinctCount = "distinct_count",
}

export enum CustomChartType {
  Bar = "bar",
  Line = "line",
  Area = "area",
  Pie = "pie",
  Donut = "donut",
  Histogram = "histogram",
  Heatmap = "heatmap",
  Treemap = "treemap",
  Sunburst = "sunburst",
}

export enum DashboardChartMode {
  AnalyticsQuery = "analytics_query",
  AgChartsOptions = "ag_charts_options",
}

export enum AnalyticsGraphFormat {
  AgCharts = "ag_charts",
  Mermaid = "mermaid",
}

export enum AnalyticsGraphDataSource {
  Requests = "requests",
  Traces = "traces",
}

export enum CustomChartTimeBucket {
  Auto = "auto",
  Day = "day",
  Week = "week",
  Month = "month",
}

export interface CustomChartSeriesSpecRequest {
  key: string;
  label: string;
  metric: CustomChartSeriesMetric;
  metricField?: string;
  distinctMetadataKey?: string;
  percentile?: number;
}

export interface DerivedRatioInsightRequest {
  type: "ratio";
  label: string;
  numeratorSeriesKey: string;
  denominatorSeriesKey: string;
}

export interface CustomChartSpecRequest {
  id: string;
  title?: string;
  chartType: CustomChartType;
  metric?: CustomChartMetric;
  metricField?: string;
  distinctMetadataKey?: string;
  percentile?: number;
  series?: CustomChartSeriesSpecRequest[];
  derivedInsights?: DerivedRatioInsightRequest[];
  groupByField?: string;
  groupByMetadataKey?: string;
  timeSeries?: boolean;
  timeBucket?: CustomChartTimeBucket;
  limit?: number;
  histogramField?: string;
  histogramInterval?: number;
  secondaryGroupByField?: string;
  secondaryGroupByMetadataKey?: string;
  hierarchyFields?: string[];
}

export interface DashboardChartViewSpecRequest {
  id: string;
  label: string;
  chartLabel?: string;
  breakdownLabel?: string;
  chartType: CustomChartType;
  metric?: CustomChartMetric;
  metricField?: string;
  distinctMetadataKey?: string;
  percentile?: number;
  series?: CustomChartSeriesSpecRequest[];
  derivedInsights?: DerivedRatioInsightRequest[];
  groupByField?: string;
  groupByMetadataKey?: string;
  timeSeries?: boolean;
  timeBucket?: CustomChartTimeBucket;
  limit?: number;
  histogramField?: string;
  histogramInterval?: number;
  secondaryGroupByField?: string;
  secondaryGroupByMetadataKey?: string;
  hierarchyFields?: string[];
}

export interface DashboardChartSpecRequest {
  id: string;
  title: string;
  mode: DashboardChartMode;
  defaultViewId?: string;
  views?: DashboardChartViewSpecRequest[];
  agChartsOptions?: Record<string, unknown>;
}

export interface CustomChartSeriesMeta {
  key: string;
  label: string;
  rawLabel?: string;
  unit: string;
}

export interface DerivedRatioInsightResponse {
  type: "ratio";
  label: string;
  numeratorSeriesKey: string;
  denominatorSeriesKey: string;
  value: number;
}

export interface CustomChartDataPoint {
  label: string;
  value?: number;
  bucketKey?: string;
  rawLabel?: string;
  x?: string;
  y?: string;
  path?: string[];
  [seriesKey: string]: string | number | string[] | undefined;
}

export interface CustomChartResponseItem {
  id: string;
  title: string;
  chartType: CustomChartType;
  data: CustomChartDataPoint[];
  series: CustomChartSeriesMeta[];
  derivedInsights?: DerivedRatioInsightResponse[];
}

export interface RequestAnalyticsCustomChartsResponse {
  success: boolean;
  chartInterval?: AnalyticsChartInterval;
  customCharts: CustomChartResponseItem[];
}

export interface AnalyticsGraphToolChartData {
  chartId: string;
  chartTitle: string;
  selectedViewId?: string;
  selectedViewLabel?: string;
  chartInterval?: AnalyticsChartInterval;
  data: CustomChartDataPoint[];
  series: CustomChartSeriesMeta[];
  derivedInsights?: DerivedRatioInsightResponse[];
}

export interface AgChartsGraphArtifact {
  format: AnalyticsGraphFormat.AgCharts;
  dataSource?: AnalyticsGraphDataSource;
  data_source?: AnalyticsGraphDataSource;
  title: string;
  filterGroup?: StructuredFilterGroupRequest;
  filter_group?: StructuredFilterGroupRequest;
  charts: DashboardChartSpecRequest[];
}

export const getAgChartsGraphArtifactFilterGroup = (
  artifact: AgChartsGraphArtifact,
): StructuredFilterGroupRequest | undefined => artifact.filterGroup ?? artifact.filter_group;

export interface MermaidGraphArtifact {
  format: AnalyticsGraphFormat.Mermaid;
  title: string;
  diagram: string;
}

export type AnalyticsGraphArtifact = AgChartsGraphArtifact | MermaidGraphArtifact;

export const getDefaultViewId = (chart: DashboardChartSpecRequest): string | undefined => {
  const views = chart.views ?? [];
  if (views.length === 0) {
    return undefined;
  }
  if (chart.defaultViewId && views.some(view => view.id === chart.defaultViewId)) {
    return chart.defaultViewId;
  }
  return views[0]?.id;
};

export const resolveDashboardChartView = (
  chart: DashboardChartSpecRequest,
  selectedViewId?: string,
): DashboardChartViewSpecRequest | undefined => {
  const views = chart.views ?? [];
  if (views.length === 0) {
    return undefined;
  }

  const defaultViewId = getDefaultViewId(chart);
  const resolvedViewId = selectedViewId ?? defaultViewId;
  return (
    views.find(view => view.id === resolvedViewId) ??
    views.find(view => view.id === defaultViewId) ??
    views[0]
  );
};

export const viewToCustomChartSpec = (
  chart: DashboardChartSpecRequest,
  view: DashboardChartViewSpecRequest,
): CustomChartSpecRequest => ({
  id: chart.id,
  title: chart.title,
  chartType: view.chartType,
  metric: view.metric,
  metricField: view.metricField,
  distinctMetadataKey: view.distinctMetadataKey,
  percentile: view.percentile,
  series: view.series,
  derivedInsights: view.derivedInsights,
  groupByField: view.groupByField,
  groupByMetadataKey: view.groupByMetadataKey,
  timeSeries: view.timeSeries,
  timeBucket: view.timeBucket,
  limit: view.limit,
  histogramField: view.histogramField,
  histogramInterval: view.histogramInterval,
  secondaryGroupByField: view.secondaryGroupByField,
  secondaryGroupByMetadataKey: view.secondaryGroupByMetadataKey,
  hierarchyFields: view.hierarchyFields,
});

export const splitGraphChartsByMode = (charts: DashboardChartSpecRequest[]) => ({
  analyticsQueryCharts: charts.filter(chart => chart.mode === DashboardChartMode.AnalyticsQuery),
  agChartsOptionsCharts: charts.filter(chart => chart.mode === DashboardChartMode.AgChartsOptions),
});
